import {
  AloftDate,
  AloftPrice,
  DEFAULT_ECONOMIC_LIFE,
  DEFAULT_ECONOMIC_LIFE_MANUFACTURED,
  FormType,
  ListingType,
  PROPERTY_GRID_SUBJECT_PROPERTY,
  PairedCostAdjustmentType,
  isDefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { OrderState, RootState } from '../../types.ts';
import { selectAivArvActiveTab } from './uiSelectors.ts';

export const selectOrder = (state: RootState): OrderState => state.order;

export const selectCompSequence = createSelector(selectOrder, ({ compSequence }) => compSequence);
export const selectRentalCompSequence = createSelector(
  selectOrder,
  (order: OrderState) => order.rentalCompSequence,
);
export const selectCompSequenceByListingType = createSelector(
  [
    selectCompSequence,
    selectRentalCompSequence,
    (_state: RootState, listingType?: ListingType) => listingType,
  ],
  (salesSequence, rentalSequence, listingType) =>
    listingType === 'rentalListing' ? rentalSequence : salesSequence,
);
export const selectPropertyIds = createSelector(
  [selectCompSequence, selectRentalCompSequence],
  (compSequence, rentalCompSequence) => [
    PROPERTY_GRID_SUBJECT_PROPERTY,
    ...compSequence,
    ...rentalCompSequence,
  ],
);

export const selectHasArvOption = createSelector(selectOrder, ({ hasArvOption }) => hasArvOption);
export const selectHasAvmOption = createSelector(selectOrder, ({ hasAvmOption }) => hasAvmOption);
export const selectHasRentalConfiguration = createSelector(
  selectOrder,
  ({ hasRentalConfiguration }) => hasRentalConfiguration,
);
export const selectIncludeCostApproach = createSelector(
  selectOrder,
  ({ includeCostApproach }) => includeCostApproach,
);

export const selectOrderFeatures = createSelector(
  selectOrder,
  ({ orderFeatures }) => orderFeatures,
);

export const selectPropertyType = createSelector(selectOrder, ({ propertyType }) => propertyType);

export const selectFormType = createSelector(selectPropertyType, (propertyType) => {
  switch (propertyType) {
    case 'multiFamily':
      return FormType.fnma1025;
    case 'singleFamilyResidence':
      return FormType.fnma1004;
    case 'manufacturedHome':
      return FormType.fnma1004c;
    default:
      return undefined;
  }
});

export const selectEffectiveDate = createSelector(
  selectOrder,
  ({ effectiveDate }) => effectiveDate,
);

export const selectIntendedUse = createSelector(selectOrder, ({ intendedUse }) => intendedUse);
export const selectIntendedUsers = createSelector(
  selectOrder,
  ({ intendedUsers }) => intendedUsers,
);

export const selectEffectiveYear = createSelector(selectEffectiveDate, (effectiveDate) => {
  const date = isDefined(effectiveDate) ? effectiveDate : AloftDate.today().toJSON();
  return AloftDate.schema.parse(date).getYear().toString();
});

export const selectPropertyTitleById = createSelector(
  [selectCompSequence, (_state, id: string) => id],
  (compSequence, id) => {
    const titles: { [id: string]: string } = { subject: 'Subject', subjectArv: 'Subject (ARV)' };
    compSequence.forEach((compID, index) => {
      titles[compID] = `Comp ${index + 1}`;
    });

    return titles[id] ?? '';
  },
);

export const selectRentalPropertyTitleById = createSelector(
  [selectRentalCompSequence, (_state, id: string) => id],
  (rentalCompSequence, id) => {
    const titles: { [id: string]: string } = { subject: 'Subject', subjectArv: 'Subject (ARV)' };
    rentalCompSequence.forEach((compID: string, index: number) => {
      titles[compID] = `Comp ${index + 1}`;
    });

    return titles[id] ?? '';
  },
);

export const selectCompIndexById = createSelector(
  [selectCompSequence, (_state, id: string) => id],
  (compSequence, id) => compSequence.findIndex((compID) => compID === id),
);

export const selectRentalCompIndexById = createSelector(
  [selectRentalCompSequence, (_state, id: string) => id],
  (rentalCompSequence, id) =>
    rentalCompSequence.findIndex((compID: string) => compID === id),
);

export const selectShouldLoadSubject = createSelector(
  [selectOrder],
  ({ subjectLoaded, id }) => isDefined(id) && id.length > 0 && !subjectLoaded,
);

export const selectShouldLoadComps = createSelector(
  [selectOrder],
  ({ compsLoaded, id }) => isDefined(id) && id.length > 0 && !compsLoaded,
);

export const selectShouldLoadAdjustmentSettings = createSelector(
  [selectOrder],
  ({ adjustmentSettingsLoaded, id }) => isDefined(id) && id.length > 0 && !adjustmentSettingsLoaded,
);
export const selectShouldLoadDepreciationSettings = createSelector(
  [selectOrder],
  ({ depreciationSettingsLoaded, id }) =>
    isDefined(id) && id.length > 0 && !depreciationSettingsLoaded,
);
export const selectShouldLoadMarketAdjustments = createSelector(
  [selectOrder],
  ({ marketAdjustmentsLoaded, id }) => isDefined(id) && id.length > 0 && !marketAdjustmentsLoaded,
);
export const selectShouldLoadMarketConditions = createSelector(
  [selectOrder],
  ({ marketConditionsLoaded, id }) => isDefined(id) && id.length > 0 && !marketConditionsLoaded,
);
export const selectShouldLoadOrderFeatures = createSelector(
  [selectOrder],
  ({ orderFeaturesLoaded, id }) => isDefined(id) && id.length > 0 && !orderFeaturesLoaded,
);

export const selectCompsToSave = createSelector([selectOrder], (order: OrderState) =>
  order.compSaveQueue[0] ? order.compSaveQueue[0].comps : [],
);

export const selectAdjustmentSettings = createSelector(
  selectOrder,
  ({ adjustmentSettings }) => adjustmentSettings,
);

export const selectDepreciationSettings = createSelector(
  selectOrder,
  ({ depreciationSettings }) => depreciationSettings,
);

export const selectCostMultiplier = (state: RootState) => state.order.costMultiplier;

export const selectCostMultiplierFormData = createSelector(
  selectCostMultiplier,
  (costMultiplier) => ({ costMultiplier: costMultiplier?.toString() }),
);

export const selectSettingsFormValues = createSelector(
  [selectAdjustmentSettings, selectDepreciationSettings, selectCostMultiplierFormData],
  (adjustmentSettings, depreciationSettings, costMultiplier) => ({
    roundingDefault: isDefined(adjustmentSettings.roundingDefault)
      ? AloftPrice.schema.parse(adjustmentSettings.roundingDefault).toDollarLike().toString()
      : undefined,
    rounding: Object.entries(adjustmentSettings.rounding).map(([adjustmentType, value]) => ({
      adjustmentType: adjustmentType as PairedCostAdjustmentType,
      value: AloftPrice.schema.parse(value).toDollarLike().toString(),
    })),
    thresholdDefault: isDefined(adjustmentSettings.thresholdDefault)
      ? AloftPrice.schema.parse(adjustmentSettings.thresholdDefault).toDollarLike().toString()
      : undefined,
    threshold: Object.entries(adjustmentSettings.threshold).map(([adjustmentType, value]) => ({
      adjustmentType: adjustmentType as PairedCostAdjustmentType,
      value: AloftPrice.schema.parse(value).toDollarLike().toString(),
    })),
    depreciateSeparately: depreciationSettings.depreciateSeparately ? 'true' : 'false',
    enableFunctionalDepreciation: depreciationSettings.enableFunctionalDepreciation
      ? 'true'
      : 'false',
    enableExternalDepreciation: depreciationSettings.enableExternalDepreciation ? 'true' : 'false',
    defaultEcoLife:
      depreciationSettings.defaultEcoLife?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeManufactured:
      depreciationSettings.defaultEcoLifeManufactured?.toString() ??
      DEFAULT_ECONOMIC_LIFE_MANUFACTURED.toString(),
    defaultEcoLifeGarage:
      depreciationSettings.defaultEcoLifeGarage?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeDetachedGarage:
      depreciationSettings.defaultEcoLifeDetachedGarage?.toString() ??
      DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeCarport:
      depreciationSettings.defaultEcoLifeCarport?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifePorch:
      depreciationSettings.defaultEcoLifePorch?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifePatio:
      depreciationSettings.defaultEcoLifePatio?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeDeck:
      depreciationSettings.defaultEcoLifeDeck?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeWorkshop:
      depreciationSettings.defaultEcoLifeWorkshop?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeShed:
      depreciationSettings.defaultEcoLifeShed?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeBarn:
      depreciationSettings.defaultEcoLifeBarn?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifePool:
      depreciationSettings.defaultEcoLifePool?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeSpa:
      depreciationSettings.defaultEcoLifeSpa?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeADU:
      depreciationSettings.defaultEcoLifeADU?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeManualCost:
      depreciationSettings.defaultEcoLifeManualCost?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    defaultEcoLifeFireplace:
      depreciationSettings.defaultEcoLifeFireplace?.toString() ?? DEFAULT_ECONOMIC_LIFE.toString(),
    costMultiplier: costMultiplier.costMultiplier,
  }),
);

export const selectMarketAdjustments = createSelector(
  selectOrder,
  ({ marketAdjustments }) => marketAdjustments,
);

export const selectDataSetVersion = (state: RootState) => state.order.datasetVersion;
export const selectCalculatorVersion = (state: RootState) => state.order.calculatorVersion;
export const selectAdjustmentVersion = (state: RootState) => state.order.adjustmentVersion;

export const selectDepreciateSeparately = (state: RootState) =>
  state.order.depreciationSettings.depreciateSeparately || false;
export const selectQCDisabledForOrder = (state: RootState) => state.order.isTraining;

export const selectIsInspectifyOrder = (state: RootState) => state.order.isInspectifyOrder;

export const selectIsArvOrder = (state: RootState) => state.order.hasArvOption;

export const selectCompType = (state: RootState, compId: string) => state.order.compTypes?.[compId];
export const selectCompTypes = (state: RootState) => state.order.compTypes;

export const selectArvFilteredCompSequence = createSelector(
  [selectCompSequence, selectIsArvOrder, selectAivArvActiveTab, selectCompTypes],
  (compSequence, isArvOrder, activeTab, compTypes) => {
    if (!isArvOrder) {
      return compSequence;
    }

    return compSequence.filter((compId) => {
      if (activeTab === 'all') return true;
      return compTypes?.[compId] === activeTab;
    });
  },
);

export const selectIsMockOrder = (state: RootState) => state.order.isMock;

export const selectInspectifyReportId = (state: RootState) => {
  if (!state.order.inspectionData?.reportId) {
    return undefined;
  }

  return state.order.inspectionData.reportId;
};

export const selectStartedAt = (state: RootState) => state.order.startedAt;

export const selectAppraisalReportDisclaimer = (state: RootState) =>
  state.order.appraisalReportDisclaimer;

export const selectSalesComparisonApproach = (state: RootState) =>
  state.order.salesComparisonApproach;
